import type { SignageComponent } from '@/config/types';
import { useWidgetData } from '@/hooks/useWidgetData';
import { transformsByWidgetType } from '@/store/transforms';
import { PlaceholderWidget } from '@/widgets/PlaceholderWidget';
import { resolveWidget } from '@/widgets/registry';
import { resolveWidgetTopic } from '@/widgets/widgetTopic';
import { WidgetBoundary } from '@/widgets/WidgetBoundary';

/**
 * One config node -> one rendered, registered, error-boundaried widget.
 *
 * Everything a container has to do per component lives here, so `FlatLayout`, `GroupWidget`
 * and SYM-492's `SliderLayout` differ only in the box they put around it.
 *
 * Three responsibilities:
 *
 * 1. **Resolve** `component.type` through the registry (widget / placeholder / nothing).
 * 2. **Register** the widget's topic and its type's transform (below).
 * 3. **Isolate** it behind `WidgetBoundary`.
 */

type WidgetSlotProps = {
  component: SignageComponent;
  /** From `useStableIds()` in the parent container — never regenerated. */
  componentId: string;
};

export function WidgetSlot({ component, componentId }: WidgetSlotProps) {
  const resolution = resolveWidget(component.type);

  // An unresolvable type renders nothing, so it must claim nothing either: registering its
  // topic would put a topic no component reads into `topicItems`, which is the list the
  // preventer asks the broker to grant read access to and the list MQTT subscribes with
  // (docs/MIGRATION.md §6, §7). A placeholder *does* register — it stands in for a real
  // subscriber, and it is what lets SYM-489 exercise the full preventer/MQTT path before a
  // single widget exists.
  const { topic, absolute } =
    resolution.kind === 'unknown'
      ? { topic: undefined, absolute: false }
      : resolveWidgetTopic(component.options);

  // **The one place a transform is wired.** The widget cannot do this itself: `WidgetProps`
  // does not carry `component.type`, by design (see the contract in `registry.ts`). The
  // widget's own `useWidgetData(componentId, options.topic)` call is then a no-op
  // registration — same component id so no duplicate-topic warning, and `registerTopic`
  // leaves an already-stored transform alone when none is passed. That only holds while both
  // resolve the topic the same way, which is why `resolveWidgetTopic` is shared rather than
  // reimplemented on each side.
  //
  // The return value is ignored on purpose: this call exists for its registration effect.
  // The widget selects its own data.
  useWidgetData(componentId, topic, transformsByWidgetType[component.type], { absolute });

  if (resolution.kind === 'unknown') return null;

  return (
    <WidgetBoundary type={component.type} componentId={componentId}>
      {resolution.kind === 'widget' ? (
        <resolution.Widget componentId={componentId} options={component.options} />
      ) : (
        <PlaceholderWidget
          componentId={componentId}
          options={component.options}
          type={component.type}
          ticket={resolution.ticket}
        />
      )}
    </WidgetBoundary>
  );
}
